import React, { useMemo, useEffect, useState } from "react";
import Sidebar from "./side-bar/side-bar";
import { MenuItem, Permission } from "./side-bar/types/menu-items";
import "./provider-main-layout.scss";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { footerItems, menuItems } from "./side-bar/types/side-bar";

//  Session timer helpers
import {
  initSessionTimers,
  clearSessionTimers,
  updateLastActivity,
  SESSION_INACTIVITY_WARNING_EVENT,
} from "../../common/session/sessionTimer"; // ⚠️ keep path consistent

//  use MUI Alert to get blue "info" look (like your inactivity message)
import { Alert, Box, Button } from "@mui/material";
import { getBookingAppointmentLeads } from "../../api/provider-section/provider-service-management";

/**
 *  Provider inactivity warning (top-right, blue)
 * - Triggered by SESSION_INACTIVITY_WARNING_EVENT
 * - Click button to stay logged in (resets activity)
 */
const ProviderSessionWarningToast: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState<number>(5 * 60 * 1000);

  useEffect(() => {
    const handler = (e: any) => {
      const detail = e?.detail;
      if (!detail || detail.userType !== "provider") return;

      setOpen(!!detail.open);
      if (detail.remainingMs != null) {
        setRemainingMs(detail.remainingMs);
      }
    };

    window.addEventListener(
      SESSION_INACTIVITY_WARNING_EVENT,
      handler as EventListener,
    );

    return () => {
      window.removeEventListener(
        SESSION_INACTIVITY_WARNING_EVENT,
        handler as EventListener,
      );
    };
  }, []);

  const minutesLeft = Math.max(Math.ceil(remainingMs / 60000), 1);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 2000, // above page content
        maxWidth: 520,
      }}
    >
      <Alert
        severity="info"
        variant="filled" //  blue filled background
        onClose={() => setOpen(false)}
        sx={{
          "& .MuiAlert-action": {
            marginRight: "4px", //  space between X and right edge
          },
        }}
      >
        {`Your session will expire in ${minutesLeft} minute(s). Click here to stay logged in.`}
      </Alert>
    </Box>
  );
};

const ProviderMainLayout: React.FC = () => {
  const SUPER_TENANT = "SuperTenant";
  const dispatch = useDispatch();

  //  Provider permissions
  const permissions = useSelector(
    (state: any) => state?.session?.providerDetails?.roles?.rolePermissions,
  );

  const providerDetails = useSelector(
    (state: any) => state?.session?.providerDetails,
  );

  /**
   *  OPTION A:
   * ProviderMainLayout owns provider session lifecycle
   */
  // useEffect(() => {
  //   const isLoggedIn =
  //     !_.isEmpty(providerDetails) &&
  //     !!localStorage.getItem("providerDetails");

  //   if (isLoggedIn) {
  //     initSessionTimers("provider");
  //   } else {
  //     clearSessionTimers("provider");
  //   }
  // }, [providerDetails]);

  const currentUserRoleId = useMemo(() => {
    return providerDetails?.roles?.id;
  }, [providerDetails]);

  const currentUserType = providerDetails?.tenantDetails?.tenantType;

  const filterMenuByPermissions = (
    menuItems: MenuItem[] | undefined,
    permissions: Permission[],
  ): MenuItem[] => {
    if (!menuItems) return [];

    return menuItems.filter((menu) => {
      const module: any = permissions?.find(
        (perm: any) => perm?.resource === menu?.moduleName,
      );

      let hasMainMenuPermission = false;

      if (!menu.moduleName) {
        hasMainMenuPermission = true;
      } else if (module) {
        hasMainMenuPermission = !module.scopes || module.scopes.read === true;
      }

      if (
        menu?.permissionRole?.length &&
        !menu.permissionRole.includes(currentUserRoleId)
      ) {
        return false;
      }

      if (menu?.onlyShowTOAFP) {
        return currentUserType === SUPER_TENANT && hasMainMenuPermission;
      }

      if (menu.subMenu) {
        menu.subMenu = filterMenuByPermissions(
          menu.subMenu,
          module?.subresources,
        );
        return hasMainMenuPermission && menu.subMenu.length > 0;
      }

      return hasMainMenuPermission;
    });
  };

  const fetchLeads = async (type: string) => {
    try {
      const response = await getBookingAppointmentLeads();
      if (response.data?.success) {
        const count = response.data?.data?.data?.totalRecords || 0;
        dispatch({
          type: `LEAD:${type}`,
          payload: { count },
        });
      }
    } catch (err) {}
  };

  useEffect(() => {
    fetchLeads("INITIALIZE_PROVIDER_LEAD_COUNT");
    const intervalLead = setInterval(() => {
      fetchLeads("SET_PROVIDER_LEAD_COUNT");
    }, 300000);

    return () => clearInterval(intervalLead);
  }, []);
  // Same cloning approach as AdminMainLayout
  const filterMenu = _.cloneDeep(menuItems);
  const footerMenu = _.cloneDeep(footerItems);

  // If permission filtering not required yet, keep as-is
  const menuFilterItems = filterMenu;
  const footerFilterItems = footerMenu;

  return (
    <>
      {/*  Global inactivity warning (top-right blue alert) */}
      <ProviderSessionWarningToast />

      <Sidebar menuItems={menuFilterItems} footerItems={footerFilterItems} />
    </>
  );
};

export default ProviderMainLayout;
